/**
 * Shared Test Coverage display vocabulary (#1442).
 *
 * The Proposed tab's Scripted bar reads its tooltip from here, and
 * every coverage pill (the strip, the Requirements covered and Defects
 * covered cards) reads one threshold-to-color rule. The dashboard no
 * longer has a Test Coverage card: its number is the second line of
 * Requirements covered, whose tooltip lives in `issueCoverage.ts`.
 *
 * The bar's label is "Scripted" and the brand name is "OttoTester"; the
 * code identifiers around the data stay
 * `candidateCoverage` (brand rule: only displayed strings change).
 */

/** Threshold cutoffs for the coverage pills, in one place so a future
 * tuning PR changes one line. `good` and above → success/green;
 * `warn` up to `good` → warning/amber; below `warn` → destructive/red. */
export const COVERAGE_THRESHOLDS = { good: 80, warn: 50 } as const

/** Tooltip for the Proposed tab's Scripted bar. The bar counts proposed
 * tests with a script in this variant; a plan alone does not count. It is
 * named "Scripted", not "Coverage", because coverage on the dashboard
 * means requirements with a passing test, and the tooltip points there. */
export const TEST_COVERAGE_TOOLTIP =
  "Scripted counts the proposed tests that have a test script in this variant, out of all of them. A proposed test with only a plan and no script isn't counted yet. For how many requirements have a passing test, see Requirements covered on the dashboard."

/** Map a 0-100 percent to its pill classes. Single source of truth for
 * the threshold → colour mapping. */
export function coveragePillClass(pct: number): string {
  if (pct >= COVERAGE_THRESHOLDS.good) {
    return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900'
  }
  if (pct >= COVERAGE_THRESHOLDS.warn) {
    return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900'
  }
  return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-900'
}
